import {z} from "zod";
import React, {useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {Form} from "../../../../Common/Components/BaseComponents/Form";
import {SelectField} from "../../../../Common/Components/BaseComponents/Select";
import {InputField} from "../../../../Common/Components/BaseComponents/Input";
import {InteractionType, Lead, LeadInteraction} from "shared-types";
import {useLeadsStore} from "../../../../Stores/LeadAndCustomer/leadsStore";

const MySwal = withReactContent(Swal);

export const interactionSchema = z.object({
    type: z.enum(["PHONE", "EMAIL", "MEETING", "TOUR", "NOTE", "DOCUMENT"], {required_error: "חובה לבחור סוג"}),
    notes: z.string().min(1, "יש להזין הערות"),
    date: z.string().refine(val => !isNaN(Date.parse(val)), "תאריך לא תקין"),
});
export type InteractionFormData = z.infer<typeof interactionSchema>;

interface InteractionFormProps {
    onSubmit: (lead: Lead) => Promise<any>;
    onCancel: () => void;
}

export const InteractionForm: React.FC<InteractionFormProps> = ({onSubmit, onCancel}) => {
    const {selectedLead, fetchLeadDetails, updateSelectedLead} = useLeadsStore();

    const {leadId} = useParams()
    useEffect(() => {
        if (!selectedLead && leadId) {
            fetchLeadDetails(leadId);
        }
    }, [selectedLead, leadId, fetchLeadDetails]);

    const nav = useNavigate();
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 border border-blue-200">
                {/* כפתור סגירה עגול בפינה */}
                <button
                    onClick={onCancel}
                    className="absolute top-4 left-4 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full w-9 h-9 flex items-center justify-center shadow transition"
                    aria-label="סגור"
                    type="button"
                >
                    ×
                </button>
                <h2 className="text-2xl font-bold text-blue-700 mb-6 text-center">הוספת אינטראקציה</h2>
                <Form<InteractionFormData>
                    label=""
                    schema={interactionSchema}
                    onSubmit={async (data) => {
                        try {
                            console.log(selectedLead);

                            if (!selectedLead?.id || !selectedLead?.email) {
                                throw new Error('Selected lead missing required data');
                            }

                            const newInteraction: LeadInteraction = {
                                ...data,
                                type: data.type as InteractionType,
                                userEmail: selectedLead.email,
                                leadId: selectedLead.id,
                                userId: selectedLead.id, // You might need to get actual user ID
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                            };

                            const temp = {
                                ...selectedLead,
                                interactions: [
                                    ...(selectedLead?.interactions || []),
                                    newInteraction
                                ]
                            } as Lead;

                            // First save to server
                            const response = await onSubmit(temp);

                            // If successful, update local state with the new interaction
                            // Note: The server should return the created interaction with its ID
                            if (response && selectedLead) {
                                const updatedInteractions = [
                                    ...(selectedLead.interactions || []),
                                    newInteraction // In real scenario, you'd use the returned interaction with proper ID
                                ];
                                const updatedLead = {...selectedLead, interactions: updatedInteractions};
                                updateSelectedLead(updatedLead);
                            }

                            MySwal.fire({
                                title: 'בוצע בהצלחה!',
                                text: 'האינטראקציה נוספה',
                                icon: 'success',
                                confirmButtonText: 'סגור',
                                customClass: {
                                    popup: 'swal2-rtl',
                                }
                            }).then(() => {
                                nav('/leadAndCustomer/leads')
                            });

                        } catch (error) {
                            MySwal.fire({
                                title: 'אירעה שגיאה',
                                text: 'ניסיון ההוספה נכשל. נסה שוב.',
                                icon: 'error',
                                confirmButtonText: 'סגור',
                                customClass: {
                                    popup: 'swal2-rtl',
                                }
                            });
                        }
                    }}
                >
                    <label className="block mb-2 font-semibold text-blue-700">סוג אינטראקציה:</label>
                    <SelectField
                        required
                        name="type"
                        label=""
                        options={[
                            {label: "סיור", value: "TOUR"},
                            {label: "שיחה", value: "PHONE"},
                            {label: "אימייל", value: "EMAIL"},
                            {label: "פגישה", value: "MEETING"},
                            {label: "הערה", value: "NOTE"},
                            {label: "מסמך", value: "DOCUMENT"},
                        ]}
                    />

                    <label className="block mb-2 font-semibold text-blue-700">תאריך:</label>
                    <InputField required name="date" label="" type="date"/>

                    <label className="block mb-2 font-semibold text-blue-700">הערות:</label>
                    <InputField required name="notes" label=""/>

                    <div className="flex justify-end gap-2 mt-8">
                        <button
                            type="button"
                            className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition"
                            onClick={onCancel}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition"
                        >
                            שמור
                        </button>
                    </div>
                </Form>
            </div>
        </div>
    );
};